/**
 * PMO → Roadmap → Portal Usage
 * Company dashboard with person / page filters that scope every curve.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { authenticatedApiCall } from '../../utils/apiClient';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import { ListenButton } from '../common/AudioGuide';

const ACCENT = '#0f766e';
const DAYS_OPTIONS = [7, 30, 90, 365];

const fmtMinutes = (seconds) => {
  const s = Number(seconds) || 0;
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
};

const fmtWhen = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-CA', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const shortLabel = (value, max = 22) => {
  const text = String(value || '').trim();
  if (text.length <= max) return text || '—';
  return `${text.slice(0, max - 1)}…`;
};

const fmtDayTick = (day) => {
  if (!day) return '';
  const [, month, date] = String(day).split('-');
  return month && date ? `${month}-${date}` : day;
};

const fmtDelta = (current, prior) => {
  const c = Number(current) || 0;
  const p = Number(prior) || 0;
  if (p <= 0) return c > 0 ? 'new vs prior period' : 'no prior period';
  const pct = Math.round(((c - p) / p) * 100);
  if (pct === 0) return 'same as prior period';
  return `${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)}% vs prior period`;
};

const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const downloadCsv = (filename, lines) => {
  const blob = new Blob([lines.map((row) => row.map(csvCell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const readUsageParams = () => {
  try {
    const q = new URLSearchParams(window.location.search);
    const days = parseInt(q.get('usageDays'), 10);
    return {
      person: (q.get('usagePerson') || '').trim().toLowerCase() || null,
      page: (q.get('usagePage') || '').trim() || null,
      days: DAYS_OPTIONS.includes(days) ? days : 30,
    };
  } catch {
    return { person: null, page: null, days: 30 };
  }
};

const writeUsageParams = ({ days, person, page }) => {
  try {
    const url = new URL(window.location.href);
    if (person) url.searchParams.set('usagePerson', person);
    else url.searchParams.delete('usagePerson');
    if (page) url.searchParams.set('usagePage', page);
    else url.searchParams.delete('usagePage');
    url.searchParams.set('usageDays', String(days));
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* ignore */
  }
};

const sortRows = (rows, spec, getters) => {
  if (!spec?.key) return rows;
  const mul = spec.dir === 'asc' ? 1 : -1;
  const get = getters[spec.key] || ((row) => row[spec.key]);
  return [...rows].sort((a, b) => {
    const av = get(a);
    const bv = get(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string' || typeof bv === 'string') {
      return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * mul;
    }
    return (Number(av) - Number(bv)) * mul;
  });
};

const Metric = ({ label, value, hint, color = ACCENT }) => (
  <div style={{
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1rem 1.15rem',
    borderLeft: `4px solid ${color}`, minWidth: 0,
  }}>
    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, letterSpacing: 0.2 }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 700, color: '#0f172a', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {hint && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{hint}</div>}
  </div>
);

const personHint = (person) => {
  if (!person) return '';
  const { topPage, topModule, kpis } = person;
  if (!kpis.views) return 'No page views in this range. If they have an account, this is a training, role, or first-screen problem.';
  if (!kpis.hasLive) return 'These rows are reconstructed from last login or edit logs. They show that a module was used, not how long they stayed.';
  if (topPage?.share >= 40) {
    return `They live on ${topPage.pageLabel}${topPage.module ? ` (${topPage.module})` : ''} — ${topPage.share}% of their time. Talk to them before changing that screen.`;
  }
  if (topModule?.module) return `Most of their time is in ${topModule.module}. That is the area to train, polish, or ask them about.`;
  return 'They move across several pages. Use the lists below to see where they actually stop.';
};

const pageHint = (page) => {
  if (!page) return '';
  if (!page.kpis.views) return 'Nobody opened this page in the range.';
  if (!page.kpis.hasLive) return 'Only reconstructed history on this page — someone edited or signed in, not that they lingered.';
  if (page.kpis.users <= 2) return `Only ${page.kpis.users} ${page.kpis.users === 1 ? 'person' : 'people'} used ${page.pageLabel}. That is a training or first-screen problem, or a page to hide.`;
  return `${page.kpis.users} people used ${page.pageLabel}. The list below is who to ask before you change it.`;
};

const PortalUsageDashboard = () => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('portalUsage', 'global', true);
  const initial = useMemo(() => {
    const params = readUsageParams();
    if (params.person) params.page = null;
    return params;
  }, []);
  const [days, setDays] = useState(initial.days);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backfillNote, setBackfillNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [pageQuery, setPageQuery] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(initial.person);
  const [selectedPageId, setSelectedPageId] = useState(initial.page);
  const [person, setPerson] = useState(null);
  const [pageDetail, setPageDetail] = useState(null);
  const [personLoading, setPersonLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [scopeError, setScopeError] = useState(null);
  const [userSort, setUserSort] = useState({ key: 'views', dir: 'desc' });
  const [pageSort, setPageSort] = useState({ key: 'views', dir: 'desc' });

  useEffect(() => {
    writeUsageParams({ days, person: selectedEmail, page: selectedPageId });
  }, [days, selectedEmail, selectedPageId]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/portal-usage/summary?days=${days}`);
      if (res.data?.success) setData(res.data.data);
      else setError(res.data?.message || 'Failed to load usage');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, days]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedEmail) {
      setPerson(null);
      return undefined;
    }
    let cancelled = false;
    const loadPerson = async () => {
      try {
        setPersonLoading(true);
        setScopeError(null);
        const res = await authenticatedApiCall(instance, accounts, 'GET', `/portal-usage/person?email=${encodeURIComponent(selectedEmail)}&days=${days}`);
        if (!cancelled) {
          if (res.data?.success) setPerson(res.data.data);
          else setScopeError(res.data?.message || 'Failed to load this person');
        }
      } catch (err) {
        if (!cancelled) setScopeError(err.response?.data?.message || err.message || 'Failed to load this person');
      } finally {
        if (!cancelled) setPersonLoading(false);
      }
    };
    loadPerson();
    return () => { cancelled = true; };
  }, [instance, accounts, selectedEmail, days]);

  useEffect(() => {
    if (!selectedPageId) {
      setPageDetail(null);
      return undefined;
    }
    let cancelled = false;
    const loadPage = async () => {
      try {
        setPageLoading(true);
        setScopeError(null);
        const res = await authenticatedApiCall(instance, accounts, 'GET', `/portal-usage/page?pageId=${encodeURIComponent(selectedPageId)}&days=${days}`);
        if (!cancelled) {
          if (res.data?.success) setPageDetail(res.data.data);
          else setScopeError(res.data?.message || 'Failed to load this page');
        }
      } catch (err) {
        if (!cancelled) setScopeError(err.response?.data?.message || err.message || 'Failed to load this page');
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    };
    loadPage();
    return () => { cancelled = true; };
  }, [instance, accounts, selectedPageId, days]);

  const runBackfill = async () => {
    try {
      setBusy(true);
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/portal-usage/backfill', { force: true });
      const result = res.data?.data || {};
      setBackfillNote(result.skipped
        ? 'Retrospective data was already loaded.'
        : `Reconstructed ${result.loginRows || 0} logins and ${result.auditRows || 0} edit events.`);
      await load();
    } catch (err) {
      setBackfillNote(err.response?.data?.message || err.message || 'Backfill failed');
    } finally {
      setBusy(false);
    }
  };

  const personOn = Boolean(selectedEmail && person && !personLoading);
  const pageOn = Boolean(selectedPageId && pageDetail && !pageLoading);
  const filtered = personOn || pageOn;
  const scopeLoading = Boolean((selectedEmail && personLoading) || (selectedPageId && pageLoading));
  const scopeName = personOn
    ? person.displayName
    : pageOn
      ? pageDetail.pageLabel
      : selectedEmail
        || data?.pages?.find((p) => p.page_id === selectedPageId)?.page_label
        || selectedPageId
        || 'Everyone';

  const userRows = personOn ? (data?.users || []) : pageOn ? (pageDetail.users || []) : (data?.users || []);
  const pageRows = personOn ? (person.pages || []) : (data?.pages || []);

  const users = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    const filteredRows = q
      ? userRows.filter((u) => `${u.display_name || ''} ${u.email || ''}`.toLowerCase().includes(q))
      : userRows;
    return sortRows(filteredRows, userSort, {
      person: (u) => u.display_name || u.email,
      views: (u) => Number(u.views) || 0,
      pages: (u) => Number(u.pages) || 0,
      seconds: (u) => Number(u.seconds) || 0,
      last_seen: (u) => u.last_seen ? new Date(u.last_seen).getTime() : 0,
    });
  }, [userRows, userQuery, userSort]);

  const pages = useMemo(() => {
    const q = pageQuery.trim().toLowerCase();
    const filteredRows = q
      ? pageRows.filter((p) => `${p.page_label || ''} ${p.page_id || ''} ${p.module || ''}`.toLowerCase().includes(q))
      : pageRows;
    return sortRows(filteredRows, pageSort, {
      page: (p) => p.page_label || p.page_id,
      views: (p) => Number(p.views) || 0,
      users: (p) => Number(p.users) || 0,
      seconds: (p) => Number(p.seconds) || 0,
      last_seen: (p) => p.last_seen ? new Date(p.last_seen).getTime() : 0,
    });
  }, [pageRows, pageQuery, pageSort]);

  const timeline = personOn ? (person.timeline || []) : pageOn ? (pageDetail.timeline || []) : (data?.timeline || []);
  const modules = personOn ? (person.modules || []) : (data?.modules || []);
  const k = data?.kpis || {};
  const pk = person?.kpis || {};
  const gk = pageDetail?.kpis || {};
  const adoption = k.activeAccounts ? Math.round((k.usersInRange / k.activeAccounts) * 100) : 0;
  const liveShare = (() => {
    const rows = data?.sources || [];
    const total = rows.reduce((n, r) => n + Number(r.views || 0), 0);
    const live = rows.filter((r) => r.source === 'spa').reduce((n, r) => n + Number(r.views || 0), 0);
    return total ? Math.round((live / total) * 100) : 0;
  })();

  const trafficData = useMemo(() => {
    const company = new Map((data?.timeline || []).map((row) => [row.day, row]));
    return timeline.map((row) => ({
      ...row,
      minutes: Math.round((Number(row.seconds) || 0) / 60),
      companyMinutes: Math.round((Number(company.get(row.day)?.seconds) || 0) / 60),
      companyViews: Number(company.get(row.day)?.views) || 0,
    }));
  }, [timeline, data]);

  const moduleBars = useMemo(() => modules.slice(0, 8).map((row) => ({
    name: shortLabel(row.module, 18),
    full: row.module,
    minutes: Math.round((Number(row.seconds) || 0) / 60),
    views: Number(row.views) || 0,
  })), [modules]);

  const pageBars = useMemo(() => [...pageRows]
    .sort((a, b) => (Number(b.seconds) || 0) - (Number(a.seconds) || 0) || (Number(b.views) || 0) - (Number(a.views) || 0))
    .slice(0, 8)
    .map((row) => ({
      id: row.page_id,
      name: shortLabel(row.page_label || row.page_id, 18),
      full: row.page_label || row.page_id,
      minutes: Math.round((Number(row.seconds) || 0) / 60),
      views: Number(row.views) || 0,
    })), [pageRows]);

  const selectPerson = (email) => {
    setSelectedPageId(null);
    setPageDetail(null);
    setSelectedEmail((current) => (current === email ? null : email));
  };

  const selectPage = (pageId) => {
    setSelectedEmail(null);
    setPerson(null);
    setSelectedPageId((current) => (current === pageId ? null : pageId));
  };

  const clearScope = () => {
    setSelectedEmail(null);
    setSelectedPageId(null);
    setPerson(null);
    setPageDetail(null);
  };

  const exportCurrent = () => {
    const label = personOn ? person.email : pageOn ? pageDetail.pageId : 'everyone';
    const people = (personOn ? data?.users || [] : pageOn ? pageDetail.users || [] : data?.users || [])
      .map((u) => [u.display_name || '', u.email, u.views, u.pages || '', Math.round((u.seconds || 0) / 60), u.last_seen || '']);
    const pageLines = (personOn ? person.pages || [] : data?.pages || [])
      .map((p) => [p.page_label || p.page_id, p.module || '', p.views, p.users || '', Math.round((p.seconds || 0) / 60)]);
    downloadCsv(`portal-usage-${label}-${days}d.csv`, [
      ['Portal Usage', scopeName, `${days} days`],
      [],
      ['People'],
      ['Name', 'Email', 'Views', 'Pages', 'Minutes', 'Last seen'],
      ...people,
      [],
      ['Pages'],
      ['Page', 'Module', 'Views', 'People', 'Minutes'],
      ...pageLines,
    ]);
  };

  return (
    <div style={{ padding: '1.5rem 1.75rem 2.5rem', maxWidth: 1440, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13, color: ACCENT, fontWeight: 700, letterSpacing: 0.4 }}>PMO</div>
          <h1 style={{ margin: '4px 0 6px', fontSize: 28, color: '#0f172a' }}>Portal Usage</h1>
          <p style={{ margin: 0, color: '#64748b', maxWidth: 760 }}>
            Click a person or a page to filter every curve. Company time stays as a faint baseline so you can see whether they were busy on a quiet day.
            Export the current view when you want a list to work from.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus />
          <ListenButton compact guideId="portal-usage-help" title="Portal Usage Help" accent={ACCENT} />
          <button type="button" onClick={() => setShowHelp(true)} style={btnStyle}>❓ Help</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        {DAYS_OPTIONS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            style={{
              ...btnStyle,
              background: days === d ? ACCENT : '#fff',
              color: days === d ? '#fff' : '#334155',
              borderColor: days === d ? ACCENT : '#cbd5e1',
            }}
          >
            {d === 365 ? '1 year' : `${d} days`}
          </button>
        ))}
        <button type="button" onClick={load} style={btnStyle} disabled={loading}>Refresh</button>
        <button type="button" onClick={exportCurrent} style={btnStyle} disabled={!data}>Export CSV</button>
        <button type="button" onClick={runBackfill} style={btnStyle} disabled={busy}>
          {busy ? 'Reconstructing…' : 'Load older history'}
        </button>
        <ScopeChip
          active={Boolean(selectedEmail || selectedPageId)}
          label={scopeLoading ? 'Loading…' : (filtered ? scopeName : 'Everyone')}
          onClear={clearScope}
        />
      </div>

      {backfillNote && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, color: '#065f46', fontSize: 13 }}>
          {backfillNote}
        </div>
      )}

      <div style={{ marginTop: 12, padding: '10px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, color: '#9a3412', fontSize: 13 }}>
        Live tracking started after this page shipped.
        Older rows are reconstructed from last login and edit logs.
        {data?.firstLiveAt ? ` Live page views began ${fmtWhen(data.firstLiveAt)}.` : ' No live page views yet.'}
        {data?.sources?.length ? ` ${liveShare}% of views in this range are live.` : ''}
        {' '}Time counts while the portal tab is open. A hidden or minimized window pauses after 15 minutes.
      </div>

      {error && <div style={{ marginTop: 16, color: '#b91c1c' }}>{error}</div>}
      {scopeError && <div style={{ marginTop: 16, color: '#b91c1c' }}>{scopeError}</div>}

      {loading && !data ? (
        <div style={{ padding: '4rem', textAlign: 'center', color: '#64748b' }}>Loading portal usage…</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginTop: 18 }}>
            {personOn ? (
              <>
                <Metric label="Time" value={fmtMinutes(pk.seconds)} hint={`${pk.sessions || 0} sessions · ${fmtDelta(pk.seconds, pk.priorSeconds)}`} color="#c2410c" />
                <Metric label="Views" value={pk.views || 0} hint={fmtDelta(pk.views, pk.priorViews)} color="#0369a1" />
                <Metric label="Days active" value={pk.daysActive || 0} hint={days === 365 ? 'in 1 year' : `in ${days} days`} color="#7c3aed" />
                <Metric label="Last seen" value={fmtWhen(pk.lastSeen)} hint={pk.hasLive ? 'Live tracking' : 'Reconstructed'} color="#0f766e" />
              </>
            ) : pageOn ? (
              <>
                <Metric label="People" value={gk.users || 0} hint={fmtDelta(gk.users, gk.priorUsers)} />
                <Metric label="Time" value={fmtMinutes(gk.seconds)} hint={fmtDelta(gk.seconds, gk.priorSeconds)} color="#c2410c" />
                <Metric label="Views" value={gk.views || 0} hint={fmtDelta(gk.views, gk.priorViews)} color="#0369a1" />
                <Metric label="Last seen" value={fmtWhen(gk.lastSeen)} hint={gk.hasLive ? 'Live tracking' : 'Reconstructed'} color="#7c3aed" />
              </>
            ) : (
              <>
                <Metric label="People in range" value={k.usersInRange || 0} hint={`${fmtDelta(k.usersInRange, k.priorUsers)} · ${k.activeAccounts || 0} accounts`} />
                <Metric label="Adoption" value={`${adoption}%`} hint="Accounts that showed up" color="#0369a1" />
                <Metric label="Yesterday" value={k.dau || 0} hint="Unique people" color="#7c3aed" />
                <Metric label="Last 7 / 30 days" value={`${k.wau || 0} / ${k.mau || 0}`} hint="Weekly / monthly active" color="#2563eb" />
                <Metric label="Time on pages" value={fmtMinutes(k.secondsInRange)} hint={`${k.sessionsInRange || 0} sessions · ${fmtDelta(k.secondsInRange, k.priorSeconds)}`} color="#c2410c" />
                <Metric label="Live views" value={`${liveShare}%`} hint="Share that is measured, not reconstructed" color="#0891b2" />
              </>
            )}
          </div>

          {personOn && (
            <p style={{ margin: '14px 0 0', padding: '10px 12px', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 8, color: '#115e59', fontSize: 13 }}>
              {personHint(person)}
            </p>
          )}
          {pageOn && (
            <p style={{ margin: '14px 0 0', padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, color: '#1e3a8a', fontSize: 13 }}>
              {pageHint(pageDetail)}
            </p>
          )}

          <div style={chartGrid}>
            <ChartCard
              title="Daily traffic"
              hint={personOn ? `Views and sessions for ${scopeName}` : pageOn ? `People and views on ${scopeName}` : 'People and page views across the company'}
            >
              <AreaChart data={trafficData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={fmtDayTick} minTickGap={22} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={(label) => label} />
                {filtered && <Legend />}
                {personOn ? (
                  <>
                    <Area type="monotone" dataKey="views" name="Views" stroke="#0369a1" fill="#bae6fd" />
                    <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#7c3aed" fill="#ddd6fe" />
                  </>
                ) : (
                  <>
                    <Area type="monotone" dataKey="users" name="People" stroke="#0f766e" fill="#99f6e4" />
                    <Area type="monotone" dataKey="views" name="Views" stroke="#0369a1" fill="#bae6fd" />
                  </>
                )}
              </AreaChart>
            </ChartCard>

            <ChartCard
              title="Daily time"
              hint={filtered ? `Open-tab minutes for ${scopeName}, with company time underneath` : 'Open-tab minutes across everyone'}
            >
              <AreaChart data={trafficData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={fmtDayTick} minTickGap={22} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value, name) => [`${value} min`, name]} labelFormatter={(label) => label} />
                {filtered && <Legend />}
                {filtered && (
                  <Area type="monotone" dataKey="companyMinutes" name="Company" stroke="#94a3b8" fill="#e2e8f0" />
                )}
                <Area type="monotone" dataKey="minutes" name={filtered ? 'Selected' : 'Time'} stroke="#c2410c" fill="#fed7aa" />
              </AreaChart>
            </ChartCard>
          </div>

          {!pageOn && (
            <div style={chartGrid}>
              <ChartCard
                title="Time by module"
                hint={personOn ? `Where ${scopeName} spends time` : 'Where the company spends time'}
                empty={!moduleBars.some((b) => b.minutes || b.views) ? 'No module time in this range.' : null}
              >
                <BarChart data={moduleBars} layout="vertical" margin={{ left: 8, right: 12, top: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={118} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value, _n, item) => [`${value} min`, item?.payload?.full || 'Time']} />
                  <Bar dataKey="minutes" name="Time" fill="#0f766e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ChartCard>

              <ChartCard
                title="Time by page"
                hint={personOn ? `Pages ${scopeName} used — click a bar to filter` : 'Busiest pages — click a bar to filter'}
                empty={!pageBars.some((b) => b.minutes || b.views) ? 'No page time in this range.' : null}
              >
                <BarChart data={pageBars} layout="vertical" margin={{ left: 8, right: 12, top: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={118} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value, _n, item) => [`${value} min`, item?.payload?.full || 'Time']} />
                  <Bar
                    dataKey="minutes"
                    name="Time"
                    fill="#0369a1"
                    radius={[0, 4, 4, 0]}
                    cursor="pointer"
                    onClick={(entry) => {
                      const id = entry?.id || entry?.payload?.id;
                      if (id) selectPage(id);
                    }}
                  />
                </BarChart>
              </ChartCard>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)', gap: 16, marginTop: 16 }}>
            <section style={cardStyle}>
              <div style={tableHeadStyle}>
                <div>
                  <h2 style={{ ...h2Style, margin: 0 }}>{pageOn ? `Who used ${scopeName}` : 'Who comes more'}</h2>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 12 }}>
                    Click a person to filter the curves. Click a column header to sort.
                  </p>
                </div>
                <input value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder="Search people" style={searchStyle} />
              </div>
              <UsageTable
                sort={userSort}
                onSort={setUserSort}
                columns={pageOn
                  ? [
                    { key: 'person', label: 'Person' },
                    { key: 'views', label: 'Views' },
                    { key: 'seconds', label: 'Time' },
                    { key: 'last_seen', label: 'Last seen' },
                  ]
                  : [
                    { key: 'person', label: 'Person' },
                    { key: 'views', label: 'Views' },
                    { key: 'pages', label: 'Pages' },
                    { key: 'seconds', label: 'Time' },
                    { key: 'last_seen', label: 'Last seen' },
                    { key: 'source', label: 'Source', sortable: false },
                  ]}
                rows={users.map((u) => ({
                  key: u.email,
                  selected: selectedEmail === u.email,
                  onClick: () => selectPerson(u.email),
                  cells: pageOn
                    ? [
                      <div key={u.email}>
                        <div style={{ fontWeight: 600 }}>{u.display_name || u.email}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{u.email}</div>
                      </div>,
                      u.views,
                      fmtMinutes(u.seconds),
                      fmtWhen(u.last_seen),
                    ]
                    : [
                      <div key={u.email}>
                        <div style={{ fontWeight: 600 }}>{u.display_name || u.email}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{u.email}</div>
                      </div>,
                      u.views,
                      u.pages,
                      fmtMinutes(u.seconds),
                      fmtWhen(u.last_seen),
                      u.has_live ? 'Live' : 'Reconstructed',
                    ],
                }))}
                empty="Nobody in this range."
              />
            </section>

            <section style={cardStyle}>
              <div style={tableHeadStyle}>
                <div>
                  <h2 style={{ ...h2Style, margin: 0 }}>{personOn ? `Pages used by ${scopeName}` : 'Which pages'}</h2>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 12 }}>
                    Click a page to filter the curves. Click a column header to sort.
                  </p>
                </div>
                <input value={pageQuery} onChange={(e) => setPageQuery(e.target.value)} placeholder="Search pages" style={searchStyle} />
              </div>
              <UsageTable
                sort={pageSort}
                onSort={setPageSort}
                columns={personOn
                  ? [
                    { key: 'page', label: 'Page' },
                    { key: 'views', label: 'Views' },
                    { key: 'seconds', label: 'Time' },
                    { key: 'last_seen', label: 'Last on it' },
                  ]
                  : [
                    { key: 'page', label: 'Page' },
                    { key: 'views', label: 'Views' },
                    { key: 'users', label: 'People' },
                    { key: 'seconds', label: 'Time' },
                  ]}
                rows={pages.map((p) => ({
                  key: p.page_id,
                  selected: selectedPageId === p.page_id,
                  onClick: () => selectPage(p.page_id),
                  cells: personOn ? [
                    <div key={p.page_id}>
                      <div style={{ fontWeight: 600 }}>{p.page_label || p.page_id}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{p.module || '—'}</div>
                    </div>,
                    p.views,
                    fmtMinutes(p.seconds),
                    fmtWhen(p.last_seen),
                  ] : [
                    <div key={p.page_id}>
                      <div style={{ fontWeight: 600 }}>{p.page_label || p.page_id}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{p.module}</div>
                    </div>,
                    p.views,
                    p.users,
                    fmtMinutes(p.seconds),
                  ],
                }))}
                empty="No pages in this range."
              />
            </section>
          </div>

          {personOn && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16, marginTop: 16 }}>
              <section style={cardStyle}>
                <h2 style={h2Style}>Recent sessions</h2>
                <UsageTable
                  columns={[{ key: 'started', label: 'Started', sortable: false }, { key: 'page', label: 'Page', sortable: false }, { key: 'time', label: 'Time', sortable: false }]}
                  rows={(person.sessions || []).map((s, i) => ({
                    key: `${s.started_at}-${i}`,
                    cells: [
                      fmtWhen(s.started_at),
                      <div key={`${s.page_id}-${i}`}>
                        <div style={{ fontWeight: 600 }}>{s.page_label || s.page_id}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{s.path || s.module || '—'}</div>
                      </div>,
                      fmtMinutes(s.duration_seconds),
                    ],
                  }))}
                  empty="No timed sessions yet — reconstructed history has views, not minutes."
                />
              </section>
              <section style={cardStyle}>
                <h2 style={h2Style}>Recent views</h2>
                <UsageTable
                  columns={[{ key: 'when', label: 'When', sortable: false }, { key: 'page', label: 'Page', sortable: false }, { key: 'source', label: 'Source', sortable: false }]}
                  rows={(person.recentViews || []).map((v, i) => ({
                    key: `${v.viewed_at}-${i}`,
                    cells: [
                      fmtWhen(v.viewed_at),
                      <div key={`${v.page_id}-${i}`}>
                        <div style={{ fontWeight: 600 }}>{v.page_label || v.page_id}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{[v.module, v.path].filter(Boolean).join(' · ')}</div>
                      </div>,
                      v.source === 'spa' ? 'Live' : v.source,
                    ],
                  }))}
                  empty="No views in this range."
                />
              </section>
            </div>
          )}

          {pageOn && (
            <section style={{ ...cardStyle, marginTop: 16 }}>
              <h2 style={h2Style}>Recent sessions on {scopeName}</h2>
              <UsageTable
                columns={[{ key: 'started', label: 'Started', sortable: false }, { key: 'person', label: 'Person', sortable: false }, { key: 'time', label: 'Time', sortable: false }]}
                rows={(pageDetail.sessions || []).map((s, i) => ({
                  key: `${s.started_at}-${i}`,
                  cells: [
                    fmtWhen(s.started_at),
                    <div key={`${s.user_email}-${i}`}>
                      <div style={{ fontWeight: 600 }}>{s.user_display_name || s.user_email}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{s.path || '—'}</div>
                    </div>,
                    fmtMinutes(s.duration_seconds),
                  ],
                }))}
                empty="No timed sessions on this page yet."
              />
            </section>
          )}

          {!filtered && (
            <section style={{ ...cardStyle, marginTop: 16 }}>
              <h2 style={h2Style}>Who has not shown up</h2>
              <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13 }}>
                Active accounts with no view in this range. Click a name to open their empty activity.
              </p>
              <UsageTable
                columns={[{ key: 'person', label: 'Person', sortable: false }, { key: 'email', label: 'Email', sortable: false }, { key: 'login', label: 'Last login', sortable: false }]}
                rows={(data?.dormant || []).map((u) => ({
                  key: u.email,
                  selected: selectedEmail === u.email,
                  onClick: () => selectPerson(u.email),
                  cells: [u.display_name || u.email, u.email, fmtWhen(u.last_login)],
                }))}
                empty="Everyone with an account showed up in this range."
              />
            </section>
          )}
        </>
      )}

      {showHelp && (
        <div style={helpOverlay} onClick={() => setShowHelp(false)}>
          <div style={helpCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 20 }}>Portal Usage help</h2>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <ListenButton compact guideId="portal-usage-help" title="Portal Usage Help" accent={ACCENT} />
                <button type="button" onClick={() => setShowHelp(false)} style={btnStyle}>×</button>
              </div>
            </div>
            <HelpBody />
          </div>
        </div>
      )}
    </div>
  );
};

const ScopeChip = ({ active, label, onClear }) => (
  <span style={active ? chipActive : chipIdle}>
    {label}
    {active && <button type="button" onClick={onClear} style={chipClose} aria-label="Clear filter">×</button>}
  </span>
);

const ChartCard = ({ title, hint, empty, children }) => (
  <section style={cardStyle}>
    <h2 style={{ ...h2Style, margin: 0 }}>{title}</h2>
    {hint && <p style={{ margin: '4px 0 12px', color: '#64748b', fontSize: 12 }}>{hint}</p>}
    <div style={{ height: 260 }}>
      {empty ? <Empty>{empty}</Empty> : (
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      )}
    </div>
  </section>
);

const HelpBody = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, color: '#334155', fontSize: 14, lineHeight: 1.55 }}>
    <div>
      <strong>Why this page exists.</strong> PMO can see which parts of the portal people actually use before we redesign, train, or retire a screen.
    </div>
    <div>
      <strong>Click a person or a page.</strong> Every curve filters. Daily time keeps a faint company line underneath so you can see whether they were busy on a quiet day. Click the same row, or the chip, to return to everyone.
    </div>
    <div>
      <strong>Sort and export.</strong> Column headers sort the people and page lists. Export CSV downloads the current filter so you can take names into a training or redesign conversation.
    </div>
    <div>
      <strong>Live vs reconstructed.</strong> After this feature shipped, every page open writes a view and open-tab time. Older history is rebuilt from last login and from edit logs.
    </div>
    <div>
      <strong>Time.</strong> We count time the portal tab is open, including while you are in another application. A hidden or minimized window pauses after 15 minutes.
    </div>
  </div>
);

const UsageTable = ({ columns, rows, empty, sort, onSort }) => {
  const cols = columns.map((c) => (typeof c === 'string' ? { key: c, label: c } : c));
  return (
    <div style={{ overflowX: 'auto', maxHeight: 420 }}>
      {rows.length === 0 ? <Empty>{empty}</Empty> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {cols.map((c) => {
                const sortable = c.sortable !== false && onSort;
                const active = sort?.key === c.key;
                return (
                  <th
                    key={c.key}
                    style={{ ...thStyle, cursor: sortable ? 'pointer' : 'default', color: active ? ACCENT : '#64748b' }}
                    onClick={() => {
                      if (!sortable) return;
                      onSort({
                        key: c.key,
                        dir: active && sort.dir === 'desc' ? 'asc' : 'desc',
                      });
                    }}
                  >
                    {c.label}{active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const cells = row.cells || row;
              const clickable = typeof row.onClick === 'function';
              return (
                <tr
                  key={row.key || i}
                  onClick={row.onClick}
                  style={{
                    background: row.selected ? '#ccfbf1' : (i % 2 ? '#f8fafc' : '#fff'),
                    cursor: clickable ? 'pointer' : 'default',
                  }}
                  onMouseEnter={(e) => { if (clickable && !row.selected) e.currentTarget.style.background = '#f0fdfa'; }}
                  onMouseLeave={(e) => { if (clickable && !row.selected) e.currentTarget.style.background = i % 2 ? '#f8fafc' : '#fff'; }}
                >
                  {cells.map((cell, j) => (
                    <td key={j} style={tdStyle}>{cell}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

const Empty = ({ children }) => (
  <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>{children}</div>
);

const btnStyle = {
  padding: '7px 12px',
  background: '#fff',
  color: '#334155',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
};

const cardStyle = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: '1rem 1.15rem',
};

const chartGrid = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: 16,
  marginTop: 16,
};

const h2Style = { margin: '0 0 12px', fontSize: 16, color: '#0f172a' };
const tableHeadStyle = { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' };
const searchStyle = { padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, minWidth: 160 };
const thStyle = { textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: 12, position: 'sticky', top: 0, background: '#fff' };
const tdStyle = { padding: '8px 6px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };
const helpOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', zIndex: 80,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
};
const helpCard = {
  background: '#fff', borderRadius: 12, padding: 20, width: 'min(640px, 100%)', boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
};
const chipIdle = {
  padding: '6px 12px',
  borderRadius: 999,
  background: '#f1f5f9',
  color: '#475569',
  fontSize: 12,
  fontWeight: 600,
};
const chipActive = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 8px 6px 12px',
  borderRadius: 999,
  background: '#ccfbf1',
  color: '#115e59',
  fontSize: 12,
  fontWeight: 700,
};
const chipClose = {
  border: 'none',
  background: 'transparent',
  color: '#115e59',
  cursor: 'pointer',
  fontSize: 16,
  lineHeight: 1,
  padding: '0 4px',
};

export default PortalUsageDashboard;
